import {invalidateQueries, q, setQueryData} from "../../state";
import {
  ActivityStreamRef,
  AuditPlanPageStatus,
  CreateReviewAction,
  DisclosurePageRef,
  BulkConfirmTailoring,
  CommitOutcome,
  DateTime,
  DisclosureDocuments,
  DisclosureSummary,
  DisclosureTailoring,
  EngagementId,
  FileId,
  SetTailoringAnswer,
  SourceId,
  SourceMin,
  TailoringHistoryEntry,
  TailoringAutofillStatus,
  TailoringQuestion,
  TprmQuestionTemplateId,
} from "platformed-client/types";
import {platformedClient} from "..";

export async function summary(engagementId: EngagementId): Promise<DisclosureSummary> {
  return await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/engagements/{engagement_id}/disclosures/summary",
    {path: {engagement_id: engagementId}},
  );
}

const invalidateSummary = (engagementId: EngagementId) =>
  invalidateQueries([q.tprm.disclosureSummary.filter(engagementId)]);

export async function setPageStatus(
  engagementId: EngagementId,
  page: DisclosurePageRef,
  status: AuditPlanPageStatus,
): Promise<void> {
  await platformedClient.internalRequest(
    "patch",
    "/region/{region}/api/tprm/engagements/{engagement_id}/disclosures/pages/status",
    {path: {engagement_id: engagementId}, body: {page, status}},
  );
  await invalidateSummary(engagementId);
}

/** Creates the page's stored state on first use. */
export async function openPageActivityStream(
  engagementId: EngagementId,
  page: DisclosurePageRef,
): Promise<ActivityStreamRef> {
  const stream = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/engagements/{engagement_id}/disclosures/pages/activity-stream",
    {path: {engagement_id: engagementId}, body: {page}},
  );
  await invalidateSummary(engagementId);
  return stream;
}

/** Checklists follow the tailoring answers and documents, and go with their deleted page. */
const checklistQueries = (engagementId: EngagementId) => [
  q.tprm.disclosureChecklists.filter(engagementId),
  q.tprm.disclosureChecklist.filter(),
  q.tprm.disclosureChecklistLineHistory.filter(),
];

/** A lock also decides whether tailoring answers can be written. */
const invalidateLockedState = (engagementId: EngagementId) =>
  invalidateQueries([q.tprm.disclosureSummary.filter(engagementId), q.tprm.disclosureTailoring.filter(engagementId)]);

export async function lockPages(engagementId: EngagementId, pages: DisclosurePageRef[]): Promise<void> {
  await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/engagements/{engagement_id}/disclosures/pages/lock",
    {path: {engagement_id: engagementId}, body: {pages}},
  );
  await invalidateLockedState(engagementId);
}

export async function unlockPages(engagementId: EngagementId, pages: DisclosurePageRef[]): Promise<void> {
  await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/engagements/{engagement_id}/disclosures/pages/unlock",
    {path: {engagement_id: engagementId}, body: {pages}},
  );
  await invalidateLockedState(engagementId);
}

export async function createPageReviews(
  engagementId: EngagementId,
  pages: DisclosurePageRef[],
  actions: CreateReviewAction[],
): Promise<void> {
  await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/engagements/{engagement_id}/disclosures/pages/reviews",
    {path: {engagement_id: engagementId}, body: {pages, actions}},
  );
  // A checklist counts its line changes since each approval.
  await invalidateQueries([
    q.tprm.disclosureSummary.filter(engagementId),
    q.reviewable.filter(),
    q.tprm.disclosureChecklist.filter(),
  ]);
}

export async function deletePages(engagementId: EngagementId, pages: DisclosurePageRef[]): Promise<void> {
  await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/engagements/{engagement_id}/disclosures/pages/delete",
    {path: {engagement_id: engagementId}, body: {pages}},
  );
  await invalidateQueries([q.tprm.disclosureSummary.filter(engagementId), ...checklistQueries(engagementId)]);
}

export async function getTailoring(engagementId: EngagementId): Promise<DisclosureTailoring> {
  return await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/engagements/{engagement_id}/disclosures/tailoring",
    {path: {engagement_id: engagementId}},
  );
}

export async function getTailoringHistory(
  engagementId: EngagementId,
  questionTemplateId: TprmQuestionTemplateId,
): Promise<TailoringHistoryEntry[]> {
  return await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/engagements/{engagement_id}/disclosures/tailoring/questions/{question_template_id}/history",
    {path: {engagement_id: engagementId, question_template_id: questionTemplateId}},
  );
}

export const tailoringQueries = (engagementId: EngagementId) => [
  q.tprm.disclosureTailoring.filter(engagementId),
  q.tprm.disclosureTailoringHistory.filter(engagementId),
  q.tprm.disclosureSummary.filter(engagementId),
  ...checklistQueries(engagementId),
];

const invalidateTailoring = (engagementId: EngagementId) => invalidateQueries(tailoringQueries(engagementId));

/** The question is patched into the cached page for instant feedback. The refetch then fixes the counts. */
const patchTailoringQuestion = (engagementId: EngagementId, updated: TailoringQuestion) =>
  setQueryData(q.tprm.disclosureTailoring(engagementId), (old: DisclosureTailoring | undefined) =>
    old == null
      ? old
      : {
          ...old,
          sections: old.sections.map(section => ({
            ...section,
            questions: section.questions.map(question =>
              question.question.question_template_id === updated.question.question_template_id ? updated : question,
            ),
          })),
        },
  );

/**
 * A refused write still refreshes the page: a `page_locked` 409 brings in the lock that explains
 * it, and a stale write the state that superseded it.
 */
async function writing<T>(write: () => Promise<T>, refresh: () => Promise<unknown>, patch?: (result: T) => void) {
  try {
    const result = await write();
    patch?.(result);
    return result;
  } finally {
    await refresh();
  }
}

const writingTailoring = <T>(engagementId: EngagementId, write: () => Promise<T>, patch?: (result: T) => void) =>
  writing(write, () => invalidateTailoring(engagementId), patch);

export async function setTailoringAnswer(
  engagementId: EngagementId,
  questionTemplateId: TprmQuestionTemplateId,
  body: SetTailoringAnswer,
): Promise<TailoringQuestion> {
  return await writingTailoring(
    engagementId,
    () =>
      platformedClient.internalRequest(
        "put",
        "/region/{region}/api/tprm/engagements/{engagement_id}/disclosures/tailoring/questions/{question_template_id}/answer",
        {path: {engagement_id: engagementId, question_template_id: questionTemplateId}, body},
      ),
    updated => patchTailoringQuestion(engagementId, updated),
  );
}

export async function resetTailoringAnswer(
  engagementId: EngagementId,
  questionTemplateId: TprmQuestionTemplateId,
): Promise<TailoringQuestion> {
  return await writingTailoring(
    engagementId,
    () =>
      platformedClient.internalRequest(
        "post",
        "/region/{region}/api/tprm/engagements/{engagement_id}/disclosures/tailoring/questions/{question_template_id}/reset",
        {path: {engagement_id: engagementId, question_template_id: questionTemplateId}},
      ),
    updated => patchTailoringQuestion(engagementId, updated),
  );
}

/**
 * It is a 409 while a run is queued or running, or before the engagement has committed accounts and
 * a tailoring questionnaire. A page lock doesn't stop it.
 */
export async function runAutofill(engagementId: EngagementId): Promise<TailoringAutofillStatus> {
  return await writingTailoring(
    engagementId,
    () =>
      platformedClient.internalRequest(
        "post",
        "/region/{region}/api/tprm/engagements/{engagement_id}/disclosures/tailoring/autofill",
        {path: {engagement_id: engagementId}},
      ),
    autofill =>
      setQueryData(q.tprm.disclosureTailoring(engagementId), (old: DisclosureTailoring | undefined) =>
        old == null ? old : {...old, autofill},
      ),
  );
}

export async function confirmTailoring(
  engagementId: EngagementId,
  body: BulkConfirmTailoring,
): Promise<DisclosureTailoring> {
  return await writingTailoring(
    engagementId,
    () =>
      platformedClient.internalRequest(
        "post",
        "/region/{region}/api/tprm/engagements/{engagement_id}/disclosures/tailoring/confirm",
        {path: {engagement_id: engagementId}, body},
      ),
    tailoring => setQueryData(q.tprm.disclosureTailoring(engagementId), tailoring),
  );
}

export async function documents(engagementId: EngagementId): Promise<DisclosureDocuments> {
  return await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/engagements/{engagement_id}/disclosures/documents",
    {path: {engagement_id: engagementId}},
  );
}

const invalidateDocuments = (engagementId: EngagementId) =>
  invalidateQueries([
    q.tprm.disclosureDocuments.filter(engagementId),
    q.tprm.sourceVersions.filter(),
    q.tprm.disclosureSummary.filter(engagementId),
    q.tprm.disclosureTailoring.filter(engagementId),
    q.tprm.scopeSources.filter(),
    q.tprm.engagementScopeBadges.filter(),
    ...checklistQueries(engagementId),
  ]);

const writingDocuments = <T>(engagementId: EngagementId, write: () => Promise<T>) =>
  writing(write, () => invalidateDocuments(engagementId));

/** The first upload creates the accounts. A later one adds a new draft of them. */
export const uploadAccounts = (engagementId: EngagementId, fileId: FileId): Promise<SourceMin> =>
  writingDocuments(engagementId, () =>
    platformedClient.internalRequest(
      "put",
      "/region/{region}/api/tprm/engagements/{engagement_id}/disclosures/accounts",
      {path: {engagement_id: engagementId}, body: {file_id: fileId}},
    ),
  );

export const addSupporting = (engagementId: EngagementId, fileIds: FileId[]): Promise<SourceMin[]> =>
  writingDocuments(engagementId, () =>
    platformedClient.internalRequest(
      "post",
      "/region/{region}/api/tprm/engagements/{engagement_id}/disclosures/supporting",
      {path: {engagement_id: engagementId}, body: {file_ids: fileIds}},
    ),
  );

export const removeSupporting = (engagementId: EngagementId, sourceId: SourceId): Promise<void> =>
  writingDocuments(engagementId, async () => {
    await platformedClient.internalRequest(
      "delete",
      "/region/{region}/api/tprm/engagements/{engagement_id}/disclosures/supporting/{source_id}",
      {path: {engagement_id: engagementId, source_id: sourceId}},
    );
  });

export const commitAccounts = (
  engagementId: EngagementId,
  asOf: DateTime,
  acceptLostExtracts = false,
): Promise<CommitOutcome> =>
  writingDocuments(engagementId, () =>
    platformedClient.internalRequest(
      "post",
      "/region/{region}/api/tprm/engagements/{engagement_id}/disclosures/accounts/commit",
      {path: {engagement_id: engagementId}, body: {as_of: asOf, accept_lost_extracts: acceptLostExtracts}},
    ),
  );
